import React, {
  useContext,
  useCallback,
  useState,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import format from 'string-template';
import { Tooltip } from '@mui/material';
import * as Sentry from '@sentry/browser';

import { ReactComponent as ReviewIcon } from '../../../../../../assets/icons/v2/arrow-right.svg';
import { ReactComponent as DuplicateIcon } from '../../../../../../assets/icons/v2/duplicate.svg';
import { PrimaryButton } from '../../../../../../components/Button/ActionButtons';
import { InfoTag } from '../../../../../../components/Tags';
import { ReactComponent as CreateIcon } from '../../../../../../assets/icons/v2/creation-plus-circle.svg';
import {
  HeaderRow,
  Title,
  TitleContainer,
} from '../../../../../../components/v2/Header';
import useQuickSearch from '../../../../../../hooks/useQuickSearch';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../../../hooks/useSessionStore';
import useLogger from '../../../../../../hooks/useLogger';
import useToast from '../../../../../hooks/useToast';
import Track from '../../../../../../Model/Track';
import { useHyroxEnabled } from '../../../../../../utils/postHog';
import GenericDataGrid from '../../../../../components/GenericDataGrid';
import LabelCheckbox from '../../../../../components/LabelCheckbox';
import MultiSelectFilter from '../../../../../components/MultiSelectFilter';
import LoadingOverlay from '../../../../../components/LoadingOverlay';
import ManageTrackContext, {
  withManageTrackContextReady,
} from '../../../../../context/ManageTrackContext';
import ExternalCoachContext from '../../../../../context/ExternalCoachContext';
import UserContext from '../../../../../../context/UserContext';
import QuickSearchToolbar from '../../../../../components/QuickSearchToolbar';
import ConfirmDialog from '../../../../../components/ConfirmDialog';
import { CoachingActivity } from '../../../../../../utils/log';
import {
  ActionType,
} from '../../utils';

import getColumns from './columns';
import {
  ChipContainer,
  StyledArchiveIcon,
  ActionButtonContainer,
} from './styles';
import texts from './texts.json';

const VISIBLE_TAGS_COUNT = 1;

const TracksTable = ({ handleActionClick }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [actionPerformed, setActionPerformed] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState([]);
  const [selectedEquipments, setSelectedEquipments] = useState([]);
  const {
    coachTracks,
    showArchivedCoachTracks,
    refetchCoachTracks,
    equipmentList,
  } = useContext(ManageTrackContext);

  const { userId: coachId } = useContext(UserContext);
  const { coachDoc } = useContext(ExternalCoachContext);
  const isComponentMountedRef = useComponentMounted();
  const { isAdmin } = useSessionStore();
  const { showToast } = useToast();
  const { logCoachingActivity } = useLogger();

  const isPublicTracksCoach = !!coachDoc?.isPublicTracksCoach;

  const renderEquipmentCell = useCallback(
    ({ row: { equipment } = {} }) => {
      if (equipment && equipment.length > 0) {
        const firstEquipmentItem = equipment.slice(0, VISIBLE_TAGS_COUNT);
        const remainingEquipment = equipment.slice(VISIBLE_TAGS_COUNT, equipment.length);
        const tooltipText = remainingEquipment.join(', ');
        return (
          <ChipContainer>
            {firstEquipmentItem.map((firstEquipment) => (
              <InfoTag key={firstEquipment}>{firstEquipment}</InfoTag>
            ))}
            {!!remainingEquipment.length && (
              <Tooltip title={tooltipText} placement="top" arrow>
                <InfoTag>{format(texts.moreEquipment, { amount: remainingEquipment.length })}</InfoTag>
              </Tooltip>
            )}
          </ChipContainer>
        );
      }
      return (
        <>
          {texts.emptyCell}
        </>
      );
    }, [],
  );

  const renderActionCell = useCallback(
    ({ row }) => {
      const onActionClick = (action) => {
        setSelectedTrack(row);
        setActionPerformed(action);
      };

      // Block editing and archiving for public tracks if the current coach is not the Public Workouts coach
      const isPublicTrack = row.isPublic === true;
      const canEditPublicTrack = isPublicTrack ? isPublicTracksCoach : true;
      const canArchivePublicTrack = isPublicTrack ? isPublicTracksCoach : true;

      return (
        <ActionButtonContainer>
          <PrimaryButton
            onClick={() => onActionClick(ActionType.DUPLICATE)}
            endIcon={<DuplicateIcon />}
            variant="info"
            size="medium"
          >
            {texts.duplicate}
          </PrimaryButton>
          <PrimaryButton
            onClick={() => handleActionClick(row, ActionType.UPDATE)}
            endIcon={<ReviewIcon />}
            variant="info"
            size="medium"
            disabled={!canEditPublicTrack}
          >
            {texts.edit}
          </PrimaryButton>
          <PrimaryButton
            onClick={() => onActionClick(row.isArchived ? ActionType.UNARCHIVE : ActionType.ARCHIVE)}
            icon={<StyledArchiveIcon />}
            variant="info"
            size="medium"
            disabled={!canArchivePublicTrack}
          >
            {row.isArchived ? texts.unarchive : texts.archive}
          </PrimaryButton>
        </ActionButtonContainer>
      );
    },
    [
      handleActionClick,
      isPublicTracksCoach,
    ],
  );

  const onActionPerformed = useCallback(async () => {
    setIsLoading(true);

    try {
      if (actionPerformed === ActionType.ARCHIVE) {
        await selectedTrack.archive();
        logCoachingActivity(CoachingActivity.ARCHIVED_TRACK, { trackId: selectedTrack.id });
      } else if (actionPerformed === ActionType.UNARCHIVE) {
        await selectedTrack.unarchive();
        logCoachingActivity(CoachingActivity.UNARCHIVED_TRACK, { trackId: selectedTrack.id });
      } else if (actionPerformed === ActionType.DUPLICATE) {
        const {
          name,
          difficulty,
          location,
          equipment = [],
          programs = [],
          type,
        } = selectedTrack.data;
        const duplicateName = `${name} ${texts.copyAppendText}`;
        const newTrackData = {
          name: duplicateName,
          coach: coachId,
          equipment,
          programs,
          createdAt: Date.now(),
          isArchived: false,
          isPublic: isPublicTracksCoach,
          ...(difficulty && { difficulty }),
          ...(location && { location }),
          ...(type && { type }),
        };

        await Track.addDoc(newTrackData);
        logCoachingActivity(CoachingActivity.CREATED_TRACK, { trackName: duplicateName });
      }
      if (isComponentMountedRef.current) {
        showToast(
          format(texts.actionSuccessMsg, {
            action: actionPerformed.toLowerCase(),
            track: selectedTrack.name,
          }),
        );
        setIsLoading(false);
        setSelectedTrack(null);
        setActionPerformed(null);
      }
    } catch (error) {
      Sentry.captureException(error);
      if (isComponentMountedRef.current) {
        showToast(texts.actionErrorMsg, { error: true });
        setIsLoading(false);
        setSelectedTrack(null);
        setActionPerformed(null);
      }
    }
  }, [
    showToast,
    selectedTrack,
    actionPerformed,
    isComponentMountedRef,
    logCoachingActivity,
    coachId,
    isPublicTracksCoach,
  ]);

  const coachOnlyTracks = useMemo(() => (
    coachTracks.filter((track) => (isPublicTracksCoach ? track.isPublic : !track.isPublic))
  ), [coachTracks, isPublicTracksCoach]);

  // Show the type column for flagged coaches, or whenever a hyrox track already
  // exists in the list (e.g. the flag was turned off after tracks were created).
  const isHyroxFlagEnabled = useHyroxEnabled(coachId);
  const showTrackType = isHyroxFlagEnabled || coachOnlyTracks.some((track) => track.isHyroxTrack);

  const difficultyOptions = useMemo(() => (
    Array.from(new Set(coachOnlyTracks.map((track) => track.difficulty).filter(Boolean)))
      .map((difficulty) => ({
        value: difficulty,
        label: difficulty,
      }))
  ), [coachOnlyTracks]);

  const columns = getColumns({
    actionsRenderCell: renderActionCell,
    equipmentRenderCell: renderEquipmentCell,
    isAdmin,
    showTrackType,
  });

  const {
    filteredRows: quickSearchRows,
    toolbarProps,
  } = useQuickSearch(coachOnlyTracks, columns);

  const filteredRows = useMemo(() => {
    let filteredTracks = quickSearchRows;
    if (selectedDifficulty.length > 0) {
      filteredTracks = filteredTracks.filter((track) => selectedDifficulty.includes(track.difficulty));
    }
    if (selectedEquipments.length > 0) {
      filteredTracks = filteredTracks.filter(
        (track) => track.equipment?.some((equipment) => selectedEquipments.includes(equipment)),
      );
    }
    return filteredTracks;
  }, [
    quickSearchRows,
    selectedDifficulty,
    selectedEquipments,
  ]);

  const onCloseConfirmModal = () => {
    setActionPerformed(null);
    setSelectedTrack(null);
  };

  return (
    <>
      <HeaderRow>
        <TitleContainer>
          <Title>{texts.title}</Title>
        </TitleContainer>
        <PrimaryButton
          onClick={() => handleActionClick(null, ActionType.CREATE)}
          icon={<CreateIcon />}
        >
          {texts.createTrack}
        </PrimaryButton>
      </HeaderRow>
      <GenericDataGrid
        data-testid="tracks-table"
        rows={filteredRows}
        columns={columns}
        components={{
          Toolbar: QuickSearchToolbar,
        }}
        componentsProps={{
          toolbar: {
            ...toolbarProps,
            placeholder: texts.searchPlaceholder,
            filterTools: [
              {
                Component: LabelCheckbox,
                id: 'archive-filter',
                props: {
                  isChecked: showArchivedCoachTracks,
                  description: texts.archivedCheckbox,
                  onChange: (showArchived) => refetchCoachTracks(showArchived),
                },
              },
              {
                Component: MultiSelectFilter,
                id: 'difficulty-filter',
                props: {
                  description: texts.filter.difficulty,
                  options: difficultyOptions,
                  initialValues: selectedDifficulty,
                  onValuesSelected: setSelectedDifficulty,
                  disableValueRender: true,
                  hideAllOption: true,
                },
              },
              {
                Component: MultiSelectFilter,
                id: 'equipment-filter',
                props: {
                  description: texts.filter.equipment,
                  options: equipmentList,
                  initialValues: selectedEquipments,
                  onValuesSelected: setSelectedEquipments,
                  disableValueRender: true,
                  hideAllOption: true,
                },
              },
            ],
          },
        }}
      />
      <LoadingOverlay isLoading={isLoading} />
      <ConfirmDialog
        isOpen={!!selectedTrack}
        onConfirm={onActionPerformed}
        onCancel={onCloseConfirmModal}
        dialogTexts={{
          title: '',
          content: format(texts.actionConfirm, selectedTrack
            ? {
              track: selectedTrack.name,
              action: actionPerformed.toLowerCase(),
            }
            : ''),
        }}
      />
    </>
  );
};

TracksTable.propTypes = {
  handleActionClick: PropTypes.func.isRequired,
};

export default compose(
  withManageTrackContextReady,
  observer,
)(TracksTable);
